import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, finalize } from 'rxjs/operators';
import { FulfillItemPayload, ScannableOrder } from '@app/services/orders/scannable-order';
import { PrintService } from '../../services/print.service';
import { ScanService } from '../../services/scan.service';
import { Subscription } from 'rxjs';
import { User } from '../../../../services/user/user.model';
import { WindowService } from '../../../../core/services/window/window.service';
import { ApiService } from '../../../../core/services/api/api.service';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MobileOrderDetails } from '../mobile-order-details/mobile-order-details';
import { ShopService } from '@app/services/shops/shop.service';
import { Order } from '@app/services/order/order.model';
import { FreightServiceType } from '@pdqdev/pdq-shared/dist/CarrierData';
import { getOrderSplitIndicesString } from '@utils/order-split-utils';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-scan-order-modal',
  templateUrl: './scan-order-modal.component.html',
  styleUrls: ['./scan-order-modal.component.scss'],
  standalone: false,
})
export class ScanOrderModalComponent implements OnInit, OnDestroy {
  @Input() order: ScannableOrder;
  rolePickNPack: boolean = false;
  fulfillItemsPayload: FulfillItemPayload[] = [];
  loader = false;
  subscription: Subscription;
  defaultImage = 'https://via.placeholder.com/70x70';
  autoFulfill: boolean;
  shopId: number;
  isTplCarrier: boolean = false;
  public showContainer: boolean;
  orderSplitIndicesText: string;

  constructor(
    private _shopsService: ShopService,
    private scanService: ScanService,
    private printService: PrintService,
    private windowService: WindowService,
    private apiService: ApiService,
    public breakpointObserver: BreakpointObserver,
    public dialogRef: MatDialogRef<any>,
    private matDialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.breakpointObserver.observe(['(max-width: 440px)']).subscribe((state: BreakpointState) => {
      this.showContainer = state.matches;
    });
    if (this.showContainer) {
      this.updatePosition();
    }

    this.isTplCarrier = this.order?.orderBatch?.selectedCarrier?.freightServiceType?.includes(
      FreightServiceType.FULFILLMENT_SERVICE_3PL,
    );

    this.subscription = this.scanService.itemScanned$.pipe(filter((barcode) => !!barcode)).subscribe((barcode) => {
      // this.scanService.toggleScanInputDialog(null);
      this.updateItemScannedQuantity(barcode);
    });
    this.scanService.roleCheck().subscribe((data: User) => {
      this.shopId = data.shopId;
      const userRoles = data.roles;
      userRoles.forEach((role) => {
        if (role === 'PICKNPACK') {
          this.rolePickNPack = true;
        }
      });
    });

    this.orderSplitIndicesText = getOrderSplitIndicesString(this.order as any, 'view');
  }

  updatePosition() {
    this.dialogRef.updateSize('100vw', '100vh');
  }

  getCarrierServiceLevelFormattedString(type: string) {
    const result = type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();

    if (result) {
      return result;
    } else {
      return type;
    }
  }

  openbulktoprint(orderId: string) {
    this.printService.getOrderLabelFileUrl(orderId).then((obs) =>
      obs.subscribe(({ url }) => {
        this.windowService.openWindow(`${this.apiService.API_URL}${url}`);
      }),
    );
  }
  openMobileDetails(order: any) {
    this.matDialog.open(MobileOrderDetails, {
      minWidth: '100%',
      data: { ...order },
    });
  }

  updateItemScannedQuantity(barcode: string, orderItemId: any = null, reducedQuantity: number = 1): void {
    this.autoFulfill = true;

    let orderItem = this.order.orderItems.find(
      (item) =>
        (item?.sku?.includes(barcode) || item?.variant?.barcode?.includes(barcode)) && item.fulfillableQuantity > 0,
    );

    if (!orderItem) {
      orderItem = this.order.orderItems.find(
        (item) => item?.sku?.includes(barcode) || item?.variant?.barcode?.includes(barcode),
      );
    }

    // if sku and variant barcode is null and no '', find orderItem by externalItem to support fulfill
    if (!orderItem && orderItemId) {
      orderItem = this.order.orderItems.find((item) => item.externalId == orderItemId);
    }

    const payloadItem = this.fulfillItemsPayload.find((item) => item.id === orderItem.externalId);

    if (!payloadItem) {
      this.fulfillItemsPayload.push({
        id: orderItem.externalId,
        quantity: reducedQuantity,
        variant_external_id: (+orderItem.variant?.externalId)!,
        product_external_id: (+orderItem.product?.externalId)!,
      });
      orderItem.fulfillableQuantity = orderItem.quantity - reducedQuantity;
    } else {
      if (orderItem.fulfillableQuantity < 1) {
        this.scanService.nextScanErroMessage(
          `Item ${barcode} cannot be scanned more. Required quantity is already fulfilled.`,
        );
        return;
      }
      payloadItem.quantity = payloadItem.quantity + reducedQuantity;
      orderItem.fulfillableQuantity = orderItem.fulfillableQuantity - reducedQuantity;
    }

    //check if all item are scanned are all their fulfillable quantity is 0 (scanned)
    if (this.fulfillItemsPayload.length == this.order.orderItems.length) {
      this.order.orderItems.forEach((order) => {
        if (order.fulfillableQuantity != 0) {
          this.autoFulfill = false;
        }
      });
      if (this.autoFulfill) {
        this.fulfillOrder();
      }
    }
  }

  updateAllItemScannedQuantity() {
    this.order.orderItems?.forEach((orderItem) => {
      this.updateItemScannedQuantity(orderItem.sku, null, orderItem.fulfillableQuantity);
    });
  }

  fulfillOrder(): void {
    this.loader = true;
    this.scanService
      .fulfillOrder(this.order, this.fulfillItemsPayload)
      .pipe(
        untilDestroyed(this),
        finalize(() => {
          this.loader = false;
        }),
      )
      .subscribe(
        (fulfilledOrder) => {
          this.scanService.toggleScanOrderSuccessDialog(fulfilledOrder);
          this.printService.updateOrderInCurrentBatch(fulfilledOrder);
        },
        (err) => {},
      );
  }

  checkIfShouldDisplayOrdersCountChip(order: Order): boolean {
    return this._shopsService.checkIfShouldDisplayOrdersCountChip(order);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
